import { useEffect, useCallback, type FC } from "react";
import { Globe, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { useStore } from "src/stores/store";
import type { Lang } from "src/types";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu";

interface LanguageSelectorProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const languages = [
  { value: "en", label: "English", code: "EN" },
  {
    value: "ar",
    label: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629",
    code: "AR",
  },
] as const;

const LanguageSelector: FC<LanguageSelectorProps> = observer(
  ({ isOpen, onToggle, onClose }) => {
    const { i18n, t } = useTranslation("common");
    const { globalStore } = useStore();
    const { dir, lang, changeLang } = globalStore;

    useEffect(() => {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    }, [lang, i18n]);

    useEffect(() => {
      document.documentElement.style.setProperty("--direction", dir);
      document.documentElement.setAttribute("dir", dir);
    }, [dir]);

    const onChangeLang = useCallback(
      async (selectedLang: string) => {
        try {
          await changeLang(selectedLang as Lang);
        } catch (error) {
          console.error("Error changing language:", error);
        }
        onClose();
      },
      [changeLang, onClose],
    );

    return (
      <DropdownMenu
        open={isOpen}
        onOpenChange={open => {
          if (!open) onClose();
          else onToggle();
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              onToggle();
            }}
            className="flex items-center gap-1.5 px-2 h-9 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            aria-label={t("language")}
          >
            <Globe className="h-4 w-4" />
            <span className="hidden md:inline text-sm font-medium text-gray-700">
              {lang.toUpperCase()}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px]">
          <DropdownMenuRadioGroup value={lang} onValueChange={onChangeLang}>
            {languages.map(language => (
              <DropdownMenuRadioItem
                key={language.value}
                value={language.value}
                className="flex items-center gap-3 cursor-pointer"
              >
                <span className="text-sm">
                  {language.label} ({language.code})
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  },
);

export default LanguageSelector;
